import { useState, useRef, useEffect, useCallback } from "react";
import { useChat, type FileAttachment } from "../hooks/useChat";
import { useCost } from "../hooks/useCost";
import { MessageBubble } from "./MessageBubble";
import { useVoice } from "../hooks/useVoice";
import { getDeveloperProjects } from "../lib/api";

const ROUTE_MODES = [
  { value: "chat.fast", label: "Fast" },
  { value: "chat.balanced", label: "Balanced" },
  { value: "chat.best", label: "Best" },
  { value: "chat.cheapest", label: "Cheapest" },
  { value: "chat.local_only", label: "Local Only" },
  { value: "code.agent", label: "Code" },
];

// Dynamic projects — loaded from API, with Platform always first
const DEFAULT_PROJECTS = [{ value: "Platform", label: "Platform", repos: 0 }];

function readFileAsBase64(file: File): Promise<FileAttachment> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.split(",")[1];
      resolve({ name: file.name, type: file.type, data: base64 });
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

interface ChatViewProps {
  sessionId?: string | null;
  onSessionChange?: (id: string | null) => void;
}

export function ChatView({ sessionId, onSessionChange }: ChatViewProps) {
  const { messages, loading, error, send, approveTools, startSession, routeMode, setRouteMode, project, setProject } = useChat(sessionId);
  const [projects, setProjects] = useState(DEFAULT_PROJECTS);

  useEffect(() => {
    getDeveloperProjects()
      .then(({ projects: dbProjects }) => {
        const totalRepos = new Set(dbProjects.flatMap((p: any) => (p.repos || []).map((r: any) => r.id || r.name))).size;
        const pills = [
          { value: "Platform", label: "Platform", repos: totalRepos },
          ...dbProjects.map((p: any) => ({ value: p.name, label: p.name, repos: (p.repos || []).length })),
        ];
        setProjects(pills);
      })
      .catch(() => {});
  }, []);
  const { daily, monthly, refresh: refreshCost } = useCost();
  const [input, setInput] = useState("");
  const [attachments, setAttachments] = useState<FileAttachment[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const voice = useVoice();

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    refreshCost();
  }, [messages.length]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 200) + "px";
    }
  }, [input]);

  const addFiles = useCallback(async (files: FileList | File[]) => {
    const newAttachments: FileAttachment[] = [];
    for (const file of Array.from(files)) {
      if (file.size > 10 * 1024 * 1024) continue; // 10MB limit
      newAttachments.push(await readFileAsBase64(file));
    }
    setAttachments((prev) => [...prev, ...newAttachments]);
  }, []);

  const handleSend = async () => {
    const text = input.trim();
    if ((!text && !attachments.length) || loading) return;
    setInput("");
    const response = await send(text || "(see attached files)", attachments.length > 0 ? attachments : undefined);
    setAttachments([]);
  };

  // Auto-read new assistant messages when voice mode is on
  useEffect(() => {
    if (!voice.voiceEnabled) return;
    const last = messages[messages.length - 1];
    if (last?.role === "assistant" && last.content && !loading) {
      voice.speak(last.content);
    }
  }, [messages.length, loading]);

  // When voice recognition produces a transcript, put it in input and auto-send
  useEffect(() => {
    if (voice.transcript && !loading) {
      setInput(voice.transcript);
      // Auto-send after a short delay to let the user see what was recognized
      const timer = setTimeout(() => {
        if (voice.transcript.trim()) {
          send(voice.transcript.trim());
          setInput("");
          voice.clearTranscript();
        }
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [voice.transcript]);

  const handlePaste = useCallback(async (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const files: File[] = [];
    for (const item of Array.from(items)) {
      if (item.type.startsWith("image/")) {
        const file = item.getAsFile();
        if (file) files.push(file);
      }
    }
    if (files.length) {
      e.preventDefault();
      await addFiles(files);
    }
  }, [addFiles]);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length) {
      await addFiles(e.dataTransfer.files);
    }
  }, [addFiles]);

  const handleNewChat = async () => {
    const pendingProjectId = sessionStorage.getItem("pending_project_id");
    sessionStorage.removeItem("pending_project_id");
    const id = await startSession(pendingProjectId || undefined);
    onSessionChange?.(id);
  };

  const lastCost = messages.filter((m) => m.cost).pop()?.cost;

  return (
    <div
      className="flex flex-col h-full relative"
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
    >
      {/* Drag overlay */}
      {dragOver && (
        <div className="absolute inset-0 bg-primary/15 border-2 border-dashed border-primary rounded-xl z-10 flex items-center justify-center">
          <span className="text-lg text-primary font-medium">Drop files here</span>
        </div>
      )}

      {/* Header */}
      <div className="flex justify-between items-center px-4 py-3 border-b border-border">
        <div className="flex items-center gap-3">
          <h1 className="font-semibold text-foreground">AI</h1>
          <button
            onClick={handleNewChat}
            className="px-3 py-1 text-xs font-medium text-muted-foreground border border-border rounded-md hover:bg-card transition-colors"
          >
            New Chat
          </button>
        </div>
        <div className="flex items-center gap-4 text-xs">
          {/* Voice toggle */}
          <button
            onClick={voice.toggleVoice}
            className={`p-1.5 rounded-md transition-colors ${voice.voiceEnabled ? "bg-primary/20 text-primary" : "text-muted-foreground hover:text-foreground"}`}
            title={voice.voiceEnabled ? "Voice mode on — tap to disable" : "Enable voice mode"}
          >
            <SpeakerIcon className="w-4 h-4" />
          </button>
          {lastCost && (
            <CostBadge cost={lastCost.session_total_usd} label="Session" />
          )}
          <CostBadge cost={daily} label="Today" />
          <CostBadge cost={monthly} label="Month" />
        </div>
      </div>

      {/* Route mode + project pills */}
      <div className="flex flex-col sm:flex-row gap-0 border-b border-border">
        {/* Route mode pills */}
        <div className="flex gap-1.5 px-4 py-2 overflow-x-auto">
          {ROUTE_MODES.map((m) => (
            <button
              key={m.value}
              onClick={() => setRouteMode(m.value)}
              className={`
                px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors
                ${routeMode === m.value
                  ? "bg-primary text-primary-foreground"
                  : "bg-card text-muted-foreground hover:text-foreground"}
              `}
            >
              {m.label}
            </button>
          ))}
        </div>
        {/* Project pills */}
        <div className="flex gap-1.5 px-4 py-2 sm:py-2 sm:border-l border-t sm:border-t-0 border-border overflow-x-auto">
          {projects.map((p) => (
            <button
              key={p.value}
              onClick={() => setProject(p.value)}
              className={`
                flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors
                ${project === p.value
                  ? "bg-accent text-accent-foreground"
                  : "bg-card text-muted-foreground hover:text-foreground"}
              `}
            >
              {p.label}
              <span className={`text-[10px] font-mono ${project === p.value ? "opacity-80" : "opacity-50"}`}>
                {p.repos}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4">
        <div className="max-w-3xl mx-auto">
          {messages.length === 0 && (
            <div className="text-center text-muted-foreground mt-20">
              <p className="text-xl font-medium mb-2">AI - Life Assistant</p>
              <p className="text-sm">Ask anything. Attach images. Every response shows its cost.</p>
            </div>
          )}
          {messages.map((msg, i) => (
            <MessageBubble key={i} msg={msg} onApproveTools={approveTools} />
          ))}
          {loading && (
            <div className="flex items-center gap-2 text-muted-foreground text-sm py-2">
              <div className="w-4 h-4 border-2 border-muted-foreground/30 border-t-muted-foreground rounded-full animate-spin" />
              Thinking...
            </div>
          )}
          {error && (
            <div className="text-destructive text-sm py-2">Error: {error}</div>
          )}
          <div ref={endRef} />
        </div>
      </div>

      {/* Attachments preview */}
      {attachments.length > 0 && (
        <div className="flex gap-2 px-4 py-2 border-t border-border flex-wrap">
          {attachments.map((att, i) => (
            <div key={i} className="relative bg-card rounded-lg p-1 border border-border">
              {att.type.startsWith("image/") ? (
                <img
                  src={`data:${att.type};base64,${att.data}`}
                  alt={att.name}
                  className="h-12 rounded"
                />
              ) : (
                <span className="text-xs text-muted-foreground px-2 py-1 block">{att.name}</span>
              )}
              <button
                onClick={() => setAttachments((a) => a.filter((_, j) => j !== i))}
                className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-destructive text-destructive-foreground rounded-full flex items-center justify-center text-xs"
              >
                x
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Input */}
      <div className="px-4 py-3 border-t border-border">
        <div className="max-w-3xl mx-auto flex gap-1.5 sm:gap-2">
          {/* File input (hidden) — accepts images + documents, camera on mobile */}
          <input
            type="file"
            ref={fileInputRef}
            multiple
            accept="image/*,.pdf,.txt,.md,.json,.csv"
            className="hidden"
            onChange={(e) => e.target.files && addFiles(e.target.files)}
          />
          {/* Attach button */}
          <button
            onClick={() => fileInputRef.current?.click()}
            className="p-2 sm:p-2.5 rounded-lg bg-card border border-border text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
            title="Attach files or take photo"
          >
            <AttachIcon className="w-5 h-5" />
          </button>
          {/* Mic button — always visible, Whisper fallback for unsupported browsers */}
          <button
            onClick={() => voice.listening ? voice.stopListening() : voice.startListening()}
            disabled={voice.transcribing}
            className={`p-2 sm:p-2.5 rounded-lg border transition-colors flex-shrink-0 ${
              voice.transcribing
                ? "bg-primary/20 border-primary/30 text-primary"
                : voice.listening
                  ? "bg-red-500/20 border-red-500/30 text-red-400 animate-pulse"
                  : "bg-card border-border text-muted-foreground hover:text-foreground"
            }`}
            title={voice.transcribing ? "Transcribing..." : voice.listening ? "Tap to stop" : `Voice input${voice.hasBrowserSTT ? "" : " (Whisper)"}`}
          >
            {voice.transcribing ? (
              <div className="w-5 h-5 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
            ) : (
              <MicIcon className="w-5 h-5" />
            )}
          </button>
          {/* Text input */}
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            onPaste={handlePaste}
            placeholder="Send a message..."
            disabled={loading}
            rows={1}
            className="flex-1 min-w-0 bg-card border border-border rounded-lg px-3 py-2 sm:py-2.5 text-foreground text-sm placeholder:text-muted-foreground resize-none outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
          />
          {/* Send button */}
          <button
            onClick={handleSend}
            disabled={loading || (!input.trim() && !attachments.length)}
            className="p-2 sm:px-4 sm:py-2.5 rounded-lg bg-success text-success-foreground font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-success/90 transition-colors flex-shrink-0"
          >
            <span className="hidden sm:inline">Send</span>
            <SendIcon className="w-5 h-5 sm:hidden" />
          </button>
        </div>
      </div>
    </div>
  );
}

function CostBadge({ cost, label }: { cost: number | undefined | null; label?: string }) {
  return (
    <div className="flex items-center gap-1.5 text-muted-foreground">
      {label && <span>{label}</span>}
      <span className="font-mono text-foreground">${(cost ?? 0).toFixed(4)}</span>
    </div>
  );
}

function AttachIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
    </svg>
  );
}

function MicIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z" />
      <path d="M19 10v2a7 7 0 01-14 0v-2" />
      <line x1="12" y1="19" x2="12" y2="23" />
      <line x1="8" y1="23" x2="16" y2="23" />
    </svg>
  );
}

function SendIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="22" y1="2" x2="11" y2="13" />
      <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
  );
}

function SpeakerIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
      <path d="M19.07 4.93a10 10 0 010 14.14" />
      <path d="M15.54 8.46a5 5 0 010 7.07" />
    </svg>
  );
}
